import React from 'react';
import { MousePointer2, GripVertical, ArrowUp, ArrowRight } from 'lucide-react';

const DragDropTutorial = ({ onComplete }) => {
    return (
        <div className="fade-in" style={{ maxWidth: "800px", margin: "0 auto", padding: "2rem 1rem" }}>
            <div className="card" style={{ padding: "0", overflow: "hidden", border: "1px solid var(--border)" }}>

                {/* Header */}
                <div style={{
                    background: "rgba(124, 58, 237, 0.1)",
                    padding: "1.5rem",
                    borderBottom: "1px solid var(--border)",
                    textAlign: "center"
                }}>
                    <h2 style={{
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        marginBottom: "0.5rem",
                        color: "var(--text-primary)"
                    }}>
                        How to Answer
                    </h2>
                    <p style={{ color: "var(--text-secondary)" }}>
                        Rank the options to build your profile.
                    </p>
                </div>

                <div style={{ padding: "2rem", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "3rem", alignItems: "center" }}>

                    {/* Animation Demo Area */}
                    <div style={{
                        position: "relative",
                        background: "rgba(15, 23, 42, 0.5)",
                        borderRadius: "12px",
                        padding: "1.5rem",
                        border: "2px dashed var(--border)",
                        height: "300px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        overflow: "hidden"
                    }}>
                        <div style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            background: "var(--border)",
                            color: "var(--text-muted)",
                            fontSize: "10px",
                            fontWeight: "bold",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em"
                        }}>
                            Demo
                        </div>

                        {/* CSS Animation Styles */}
                        <style>{`
                @keyframes handMove {
                    0%, 100% { transform: translate(120%, 160%); opacity: 1; } /* Start at bottom item */
                    20% { transform: translate(120%, 160%) scale(0.9); } /* Grab */
                    50% { transform: translate(120%, 0%) scale(0.9); } /* Move to top */
                    60% { transform: translate(120%, 0%) scale(1); } /* Release */
                    75% { opacity: 0; } /* Fade out */
                    80% { transform: translate(120%, 160%); opacity: 0; } /* Reset pos */
                    85% { opacity: 1; } /* Fade in */
                }
                @keyframes itemSwap {
                    0%, 55% { transform: translateY(0); }
                    56% { transform: translateY(100%); } /* Snap others down */
                    100% { transform: translateY(100%); }
                }
                @keyframes itemMoveUp {
                    0%, 20% { transform: translateY(200%); z-index: 20; border-color: var(--primary-purple); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); background-color: rgba(30, 41, 59, 1); }
                    50%, 100% { transform: translateY(0%); z-index: 20; border-color: var(--primary-purple); box-shadow: 0 0 0 0 transparent; background-color: rgba(30, 41, 59, 0.5); }
                }
                .demo-hand {
                    animation: handMove 3.5s infinite ease-in-out;
                }
                .demo-item-active {
                    animation: itemMoveUp 3.5s infinite ease-in-out;
                }
                .demo-item-other {
                    animation: itemSwap 3.5s infinite ease-in-out;
                }
             `}</style>

                        <div style={{ position: "relative", width: "100%", maxWidth: "260px", margin: "0 auto", userSelect: "none", pointerEvents: "none" }}>

                            {/* Background Rank Slots (Static Numbers) */}
                            <div style={{ position: "absolute", right: "0", top: "0", width: "32px", height: "52px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "0" }}>
                                <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(16, 185, 129, 0.1)", color: "var(--success-green)", border: "1px solid var(--success-green)", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>1</div>
                            </div>
                            <div style={{ position: "absolute", right: "0", top: "64px", width: "32px", height: "52px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "0" }}>
                                <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(245, 158, 11, 0.1)", color: "var(--warning-amber)", border: "1px solid var(--warning-amber)", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>2</div>
                            </div>
                            <div style={{ position: "absolute", right: "0", top: "128px", width: "32px", height: "52px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "0" }}>
                                <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(244, 63, 94, 0.1)", color: "var(--error-red)", border: "1px solid var(--error-red)", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>3</div>
                            </div>

                            {/* Visual Fake Items */}
                            <div className="demo-item-other" style={{ background: "rgba(30, 41, 59, 0.5)", padding: "0.75rem", borderRadius: "8px", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "0.75rem", opacity: "0.9", paddingRight: "2.5rem", position: "relative", zIndex: "10", height: "52px", marginBottom: "12px" }}>
                                <GripVertical style={{ color: "var(--text-muted)", width: "16px", height: "16px" }} />
                                <div style={{ height: "8px", width: "64px", background: "var(--border)", borderRadius: "4px" }}></div>
                            </div>
                            <div className="demo-item-other" style={{ background: "rgba(30, 41, 59, 0.5)", padding: "0.75rem", borderRadius: "8px", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "0.75rem", opacity: "0.9", paddingRight: "2.5rem", position: "relative", zIndex: "10", height: "52px", marginBottom: "12px" }}>
                                <GripVertical style={{ color: "var(--text-muted)", width: "16px", height: "16px" }} />
                                <div style={{ height: "8px", width: "96px", background: "var(--border)", borderRadius: "4px" }}></div>
                            </div>

                            {/* The Moving Item */}
                            <div className="demo-item-active" style={{ position: "absolute", top: "0", width: "100%", background: "rgba(30, 41, 59, 0.5)", padding: "0.75rem", borderRadius: "8px", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: "0.75rem", paddingRight: "2.5rem", height: "52px" }}>
                                <GripVertical style={{ color: "var(--primary-purple)", width: "16px", height: "16px" }} />
                                <span style={{ fontSize: "12px", fontWeight: "bold", color: "var(--primary-purple-light)" }}>Most like me</span>
                                <ArrowUp style={{ width: "12px", height: "12px", color: "var(--primary-purple)", marginLeft: "auto" }} />
                            </div>

                            {/* The Hand Cursor */}
                            <div className="demo-hand" style={{ position: "absolute", top: "4px", left: "0", zIndex: "30", color: "white" }}>
                                <MousePointer2 style={{ width: "32px", height: "32px", fill: "var(--text-primary)", stroke: "var(--bg-dark)" }} />
                            </div>
                        </div>
                    </div>

                    {/* Instructions Side */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                                <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "rgba(16, 185, 129, 0.1)", color: "var(--success-green)", border: "1px solid var(--success-green)", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>1</div>
                                <div>
                                    <h3 style={{ fontWeight: "700", color: "var(--text-primary)", marginBottom: "0.25rem" }}>Top is Green</h3>
                                    <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                                        Drag the statement <strong style={{ color: "var(--success-green)" }}>Most Like You</strong> to the #1 spot.
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                                <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "rgba(245, 158, 11, 0.1)", color: "var(--warning-amber)", border: "1px solid var(--warning-amber)", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>2</div>
                                <div>
                                    <h3 style={{ fontWeight: "700", color: "var(--text-primary)", marginBottom: "0.25rem" }}>Middle is Mixed</h3>
                                    <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                                        Arrange the middle items based on preference.
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                                <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "rgba(244, 63, 94, 0.1)", color: "var(--error-red)", border: "1px solid var(--error-red)", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>4</div>
                                <div>
                                    <h3 style={{ fontWeight: "700", color: "var(--text-primary)", marginBottom: "0.25rem" }}>Bottom is Red</h3>
                                    <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                                        The statement <strong style={{ color: "var(--error-red)" }}>Least Like You</strong> goes at the bottom.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={onComplete}
                            className="btn-primary"
                            style={{ width: "100%", marginTop: "1rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}
                        >
                            Start Assessment
                            <ArrowRight style={{ width: "20px", height: "20px" }} />
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DragDropTutorial;
